import { supabase } from "@/lib/supabaseClient";
import { safeSupabaseQuery } from "@/lib/supabaseUtils";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import { fetchScopeTenantIds } from "@/lib/mergedScope";

/**
 * SUB-4.6 · The accounts the bell covers on this call.
 *
 * `20260813w` widened the three `notifications` policies to
 * `tenant_id = ANY (merged_scope_tenant_ids())`, and §4 of the Phase 3 brief is
 * explicit that widening one of the two layers changes nothing: this `.in()` is
 * the other layer. In single scope the array has one element and the query is
 * byte for byte the `.eq()` it replaced.
 *
 * Falls back to the account already resolved, so a failed status call narrows.
 */
async function bellScopeTenantIds(tenantId: string | null): Promise<string[]> {
  const ids = await fetchScopeTenantIds();
  if (ids.length > 1) return ids;
  return tenantId ? [tenantId] : ids;
}

/**
 * SUB-4.6 · Which account does a notification about this project belong to?
 *
 * D29: the record's, never the actor's session. In merged scope
 * `getCurrentTenantId()` is the ANCHOR, so an Agency Admin commenting on a
 * client's project would stamp the parent account on a notification about a
 * child record: the client's own followers could not read it (their policy pins
 * their account) and the email would go out regardless, because email does not
 * pass through RLS. `20260813w` refuses that row outright; this is the half
 * that stops it being written.
 */
async function tenantIdForProject(projectId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("projects")
    .select("tenant_id")
    .eq("id", projectId)
    .maybeSingle();

  if (error) return null;
  return (data as { tenant_id?: string } | null)?.tenant_id ?? null;
}

export interface Notification {
  id: string;
  user_id: string;
  tenant_id: string;
  type: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  read: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationInput {
  user_id: string;
  type: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  project_id?: string;
  project_name?: string;
  project_type?: string;
  sender_id?: string;
  sender_name?: string;
  link?: string;
  skipEmail?: boolean;
  /**
   * SUB-4.6 · D29. The account the notified RECORD belongs to. Omit it and the
   * actor's current account is used, which is right in single scope and is the
   * anchor rather than the record's account in merged scope.
   */
  tenant_id?: string;
}

export class NotificationService {
  /**
   * Get user notifications with caching and explicit tenant filtering
   */
  static async getUserNotifications(
    userId: string,
    limit: number = 50
  ): Promise<Notification[]> {
    let tenantId: string | null = null;

    // Try multiple times with increasing delays for tenant context loading
    const maxRetries = 3;
    const retryDelays = [0, 200, 500]; // 0ms, 200ms, 500ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        }

        tenantId = await getCurrentTenantId();

        if (tenantId) {
          break; // Success, exit retry loop
        } else if (attempt < maxRetries - 1) {
          continue;
        }
      } catch (error) {
        if (attempt === maxRetries - 1) {
          return [];
        }
      }
    }

    if (!tenantId) {
      return [];
    }

    const scopeIds = await bellScopeTenantIds(tenantId);

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", userId)
          .in("tenant_id", scopeIds)
          .order("created_at", { ascending: false })
          .limit(limit);
      },
      `notifications-user-${userId}-${scopeIds.join(",")}`,
      { enableCache: false } // Disable caching for notifications to prevent stale data issues
    );

    if (result?.error) {
      throw result.error;
    }

    return result?.data || [];
  }

  /**
   * Get unread notifications count with caching and tenant filtering
   */
  static async getUnreadCount(userId: string): Promise<number> {
    let tenantId: string | null = null;

    // Try multiple times with increasing delays for tenant context loading
    const maxRetries = 3;
    const retryDelays = [0, 200, 500]; // 0ms, 200ms, 500ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        }

        tenantId = await getCurrentTenantId();

        if (tenantId) {
          break; // Success, exit retry loop
        } else if (attempt < maxRetries - 1) {
          continue;
        }
      } catch (error) {
        if (attempt === maxRetries - 1) {
          return 0;
        }
      }
    }

    if (!tenantId) {
      return 0;
    }

    const scopeIds = await bellScopeTenantIds(tenantId);

    const result = await safeSupabaseQuery(
      async () => {
        return await supabase
          .from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userId)
          .in("tenant_id", scopeIds)
          .eq("read", false);
      },
      `notifications-unread-count-${userId}-${scopeIds.join(",")}`,
      { enableCache: false } // Disable caching for unread count to ensure accuracy
    );

    if (result?.error) {
      throw result.error;
    }

    // For count queries, Supabase returns { count: number, error: any }
    return (result?.data as any)?.count || 0;
  }

  /**
   * Mark a notification as read with caching and tenant filtering
   */
  static async markAsRead(
    notificationId: string,
    userId: string
  ): Promise<void> {
    let tenantId: string | null = null;

    // Try multiple times with increasing delays for tenant context loading
    const maxRetries = 3;
    const retryDelays = [0, 200, 500]; // 0ms, 200ms, 500ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        }

        tenantId = await getCurrentTenantId();

        if (tenantId) {
          break; // Success, exit retry loop
        } else if (attempt < maxRetries - 1) {
          continue;
        }
      } catch (error) {
        if (attempt === maxRetries - 1) {
          throw new Error('Failed to get tenant ID for markAsRead after all retries');
        }
      }
    }

    if (!tenantId) {
      throw new Error('No tenant ID available for markAsRead after all retries');
    }

    const scopeIds = await bellScopeTenantIds(tenantId);

    const result = await safeSupabaseQuery(async () => {
      return await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId)
        .eq("user_id", userId)
        .in("tenant_id", scopeIds);
    }, `notifications-mark-read-${notificationId}-${userId}-${scopeIds.join(",")}`);

    if (result?.error) {
      throw result.error;
    }
  }

  /**
   * Mark a notification as unread with caching and tenant filtering
   */
  static async markAsUnread(
    notificationId: string,
    userId: string
  ): Promise<void> {
    const tenantId = await getCurrentTenantId();
    const scopeIds = await bellScopeTenantIds(tenantId);

    const result = await safeSupabaseQuery(async () => {
      return await supabase
        .from("notifications")
        .update({ read: false })
        .eq("id", notificationId)
        .eq("user_id", userId)
        .in("tenant_id", scopeIds);
    }, `notifications-mark-unread-${notificationId}-${userId}-${scopeIds.join(",")}`);

    if (result?.error) {
      throw result.error;
    }
  }

  /**
   * Mark all user notifications as read with caching and tenant filtering
   */
  static async markAllAsRead(userId: string): Promise<void> {
    const tenantId = await getCurrentTenantId();
    const scopeIds = await bellScopeTenantIds(tenantId);

    const result = await safeSupabaseQuery(async () => {
      return await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", userId)
        .in("tenant_id", scopeIds)
        .eq("read", false);
    }, `notifications-mark-all-read-${userId}-${scopeIds.join(",")}`);

    if (result?.error) {
      throw result.error;
    }
  }

  /**
   * Create a new notification with tenant filtering
   */
  static async createNotification(
    notification: Omit<Notification, "id" | "created_at" | "updated_at">
  ): Promise<Notification> {
    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(async () => {
      return await supabase
        .from("notifications")
        .insert({
          ...notification,
          // SUB-4.6 / D29: an account supplied by the caller is the record's
          // account and outranks the session's, which in merged scope is the
          // anchor rather than the account the record lives in.
          tenant_id: notification.tenant_id ?? tenantId,
        })
        .select()
        .single();
    }, `notifications-create-${notification.user_id}-${tenantId}`);

    if (result?.error) {
      throw result.error;
    }

    if (!result?.data) {
      throw new Error("Failed to create notification: No data returned");
    }

    return result.data;
  }

  /**
   * Delete a notification with tenant filtering
   */
  static async deleteNotification(
    notificationId: string,
    userId: string
  ): Promise<void> {
    const tenantId = await getCurrentTenantId();

    const result = await safeSupabaseQuery(async () => {
      return await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId)
        .eq("user_id", userId)
        .eq("tenant_id", tenantId);
    }, `notifications-delete-${notificationId}-${userId}-${tenantId}`);

    if (result?.error) {
      throw result.error;
    }
  }

  /**
   * Subscribe to real-time notification changes with tenant filtering
   */
  static subscribeToNotifications(
    userId: string,
    callback: (payload: any) => void,
    tenantId?: string
  ) {
    // Create unique channel name to avoid conflicts
    const channelName = `notifications-${userId}-${tenantId || 'all'}`;
    
    return supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // Filter by tenant_id if provided
          if (
            tenantId &&
            payload.new &&
            typeof payload.new === "object" &&
            "tenant_id" in payload.new
          ) {
            const notification = payload.new as { tenant_id: string };
            if (notification.tenant_id !== tenantId) {
              return; // Skip notifications from other tenants
            }
          }
          
          callback(payload);
        }
      )
      .subscribe();
  }
}

// Compatibility layer for existing imports
export const notificationService = {
  async createNotification(
    input: CreateNotificationInput
  ): Promise<Notification | null> {
    try {
      // SUB-4.6 / D29: the record's account when the caller knows it.
      const tenantId = input.tenant_id ?? (await getCurrentTenantId());

      // Use the edge function to ensure consistent notification creation and email sending
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          user_id: input.user_id,
          type: input.type,
          title: input.title,
          message: input.message,
          metadata: {
            ...input.metadata,
            project_id: input.project_id,
            project_name: input.project_name,
            project_type: input.project_type,
            sender_id: input.sender_id,
            sender_name: input.sender_name,
            link: input.link,
          },
          sender_id: input.sender_id,
          sender_name: input.sender_name,
          link: input.link,
          tenant_id: tenantId
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to create notification: ${error}`);
      }

      const result = await response.json();
      return result.notification || null;
    } catch (error) {
      throw error;
    }
  },

  async getNotifications(userId: string): Promise<Notification[]> {
    return await NotificationService.getUserNotifications(userId);
  },

  async markAsRead(notificationId: string): Promise<void> {
    // Get current user ID from auth
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    return await NotificationService.markAsRead(notificationId, user.id);
  },

  async markAsUnread(notificationId: string): Promise<void> {
    // Get current user ID from auth
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    return await NotificationService.markAsUnread(notificationId, user.id);
  },

  async markAllAsRead(userId: string): Promise<void> {
    return await NotificationService.markAllAsRead(userId);
  },
};

// Utility to notify project followers of stage moves, comments, notes, or follower changes
export async function notifyProjectFollowers({
  projectId,
  type,
  projectName,
  projectType,
  senderId,
  senderName,
  message,
  metadata = {},
  link,
  isInternalComment = false,
}: {
  projectId: string;
  type: "project_created" | "project_stage_move" | "project_comment" | "project_note" | "project_follower";
  projectName?: string;
  projectType?: string;
  senderId?: string;
  senderName?: string;
  message: string;
  metadata?: Record<string, any>;
  link?: string;
  isInternalComment?: boolean;
}) {
  // SUB-4.6 · AC1/AC3 · D29. The notification belongs to the account the
  // PROJECT is in, not to whichever account the actor's session is resolving to.
  // When an Agency Admin acts on a client's project, that client's followers
  // must be notified in their own account, exactly as for a local user's action.
  // The session account remains the fallback for a project that cannot be read.
  const tenantId = (await tenantIdForProject(projectId)) ?? (await getCurrentTenantId());

  // 1. Get all followers for the project
  const { data: followers, error: followersError } = await supabase
    .from("project_followers")
    .select("user_id, users(id, first_name, last_name, email)")
    .eq("related_entity_type", "project")
    .eq("related_entity_id", projectId);

  if (followersError) {
    return;
  }
  if (!followers || followers.length === 0) return;

  // 2. For each follower, check notification preferences and send notification if enabled
  for (const follower of followers) {
    const userId = follower.user_id;

    // Fetch notification preference for this type (tenant-scoped)
    const { data: pref, error: prefError } = await supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", userId)
      .eq("notification_type", type)
      .eq("tenant_id", tenantId)
      .maybeSingle();

    if (prefError) {
      continue;
    }

    if (pref && pref.in_app_enabled === false && pref.email_enabled === false) {
      // User has disabled both in-app and email notifications for this type
      continue;
    }

    // Prepare enhanced metadata with template variables
    const enhancedMetadata: Record<string, any> = {
      ...metadata,
      project_id: projectId,
      project_name: projectName,
      project_type: projectType,
      sender_id: senderId,
      sender_name: senderName,
      is_internal: isInternalComment,
    };

    // Add type-specific template variables
    if (type === "project_comment") {
      enhancedMetadata["comment_text"] = message;
      enhancedMetadata["message_content"] = message; // Alternative field name
    } else if (
      type === "project_stage_move" &&
      metadata.old_stage &&
      metadata.new_stage
    ) {
      enhancedMetadata["old_stage"] = metadata.old_stage;
      enhancedMetadata["new_stage"] = metadata.new_stage;
    } else if (type === "project_follower" && metadata.follower_name) {
      enhancedMetadata["follower_name"] = metadata.follower_name;
    }

    await notificationService.createNotification({
      user_id: userId,
      type,
      title:
        type === "project_created"
          ? `New project: ${projectName}`
          : type === "project_stage_move"
            ? `Project moved: ${projectName}`
            : type === "project_follower"
              ? `Follower update: ${projectName}`
              : `New comment: ${projectName}`,
      message,
      metadata: enhancedMetadata,
      project_id: projectId,
      sender_id: senderId,
      sender_name: senderName,
      project_name: projectName,
      project_type: projectType,
      link,
      tenant_id: tenantId ?? undefined,
    });
  }
}

// Utility to notify organization followers
export async function notifyOrganizationFollowers({
  organizationId,
  type,
  organizationName,
  senderId,
  senderName,
  message,
  metadata = {},
  link,
}: {
  organizationId: string;
  type:
    | "organization_update"
    | "organization_comment"
    | "organization_follower"
    | "organization_stage_move";
  organizationName?: string;
  senderId?: string;
  senderName?: string;
  message: string;
  metadata?: Record<string, any>;
  link?: string;
}) {
  // SUB-4.6 · D29, the organization half. ⚠️ `organizations_select` was NOT
  // widened by `20260813s` and is not widened here, so in merged scope this read
  // returns nothing for a sub-account's organization and the fallback applies.
  // That is a boundary, not a bug: organization notifications are single-account
  // until the organizations list itself is merged.
  const { data: orgRow } = await supabase
    .from("organizations")
    .select("tenant_id")
    .eq("id", organizationId)
    .maybeSingle();
  const tenantId =
    (orgRow as { tenant_id?: string } | null)?.tenant_id ?? (await getCurrentTenantId());

  // 1. Get all followers for the organization
  const { data: followers, error: followersError } = await supabase
    .from("project_followers")
    .select("user_id, users(id, first_name, last_name, email)")
    .eq("related_entity_type", "organization")
    .eq("related_entity_id", organizationId);

  if (followersError) {
    return;
  }
  if (!followers || followers.length === 0) return;

  // 2. For each follower, check notification preferences and send notification if enabled
  for (const follower of followers) {
    const userId = follower.user_id;

    // Fetch notification preference for this type (tenant-scoped)
    const { data: pref, error: prefError } = await supabase
      .from("notification_preferences")
      .select("*")
      .eq("user_id", userId)
      .eq("notification_type", type)
      .eq("tenant_id", tenantId)
      .maybeSingle();

    if (prefError) {
      continue;
    }

    if (pref && pref.in_app_enabled === false && pref.email_enabled === false) {
      // User has disabled both in-app and email notifications for this type
      continue;
    }

    // Prepare enhanced metadata with template variables
    const enhancedMetadata: Record<string, any> = {
      ...metadata,
      organization_id: organizationId,
      organization_name: organizationName,
      sender_id: senderId,
      sender_name: senderName,
    };

    await notificationService.createNotification({
      user_id: userId,
      type,
      title:
        type === "organization_update"
          ? `Organization updated: ${organizationName}`
          : type === "organization_follower"
            ? `Follower update: ${organizationName}`
            : `New comment: ${organizationName}`,
      message,
      metadata: enhancedMetadata,
      sender_id: senderId,
      sender_name: senderName,
      link,
      tenant_id: tenantId ?? undefined,
    });
  }
}
